"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

const securityItems = [
  { title: "Structured Compliance Monitoring", desc: "Continuous oversight of operational parameters and platform alignment." },
  { title: "Infrastructure Stability Protocols", desc: "Multi-layer systems designed to maintain consistent operational uptime." },
  { title: "Redundancy-Based Deployment", desc: "Structured redeployment support ensures operational continuity." },
  { title: "Continuous Oversight Systems", desc: "24/7 monitoring with real-time operational status tracking." },
];

export default function SecuritySection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#0B0F14]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="tag-badge mx-auto mb-6 inline-flex">Security & Compliance</div>
          <h2 className="section-title text-[#F0F4F8]">
            Operational <span className="text-[#00E676]">Integrity.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-20 reveal reveal-delay-1">
          {securityItems?.map((item) => (
            <div key={item?.title} className="card-neon rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div
                  className="w-8 h-8 rounded-lg bg-[rgba(0,230,118,0.08)] border border-[rgba(0,230,118,0.15)] flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <span
                    className="w-2 h-2 rounded-full bg-[#00E676]"
                    style={{ boxShadow: "0 0 6px rgba(0,230,118,0.8)" }}
                  />
                </div>
                <div>
                  <h4 className="text-[#F0F4F8] font-bold text-sm mb-1">{item?.title}</h4>
                  <p className="text-[#6B7A8D] text-sm leading-relaxed">{item?.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Urgency CTA */}
        <div
          className="reveal reveal-delay-2 rounded-2xl border border-[rgba(0,230,118,0.2)] bg-[#11161D] p-12 text-center relative overflow-hidden"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,230,118,0.04) 0%, transparent 70%)",
            }}
          />
          <div className="relative z-10">
            <div className="tag-badge mx-auto mb-6 inline-flex">Onboarding Currently Open</div>
            <h2 className="section-title text-[#F0F4F8] mb-4">
              Remove Scaling <span className="text-[#00E676]">Limits.</span>
            </h2>
            <p className="text-[#6B7A8D] text-base max-w-xl mx-auto mb-10 leading-relaxed">
              We operate a controlled onboarding process to maintain infrastructure performance.
              Capacity is allocated on a reviewed basis.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="btn-solid-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
              >
                Initiate Infrastructure Review →
              </Link>
              <Link
                href="/pricing"
                className="btn-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}